
import apiClient from './client';

export interface QueueStrategyAssignment {
  id: string;
  queueId: string;
  queueName?: string;
  isPrimary: boolean;
  activeFrom?: string | null;
  activeTo?: string | null;
}

export interface QueueStrategyRulePayload {
  id?: string;
  name: string;
  description?: string | null;
  orderIndex?: number;
  enabled?: boolean;
  condition: Record<string, unknown>;
  effectType?: string;
  effectPayload?: Record<string, unknown>;
  weight?: number;
}

export interface QueueStrategy {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  mode: 'scoring' | 'rule_order' | 'hybrid';
  status: 'draft' | 'active' | 'archived';
  defaultPriorityScore: number;
  fallbackStrategyId?: string | null;
  metadata?: Record<string, unknown> | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  rules: QueueStrategyRulePayload[];
  assignments: QueueStrategyAssignment[];
}

export interface QueueStrategyListResponse {
  strategies: QueueStrategy[];
  capabilities: string[];
}

export interface QueueStrategyRequest {
  name: string;
  description?: string | null;
  mode?: 'scoring' | 'rule_order' | 'hybrid';
  status?: 'draft' | 'active' | 'archived';
  defaultPriorityScore?: number;
  rules: QueueStrategyRulePayload[];
  queueIds?: string[];
  metadata?: Record<string, unknown>;
}

export interface DryRunRequest {
  queueId: string;
  sampleSize?: number;
}

export interface QueueStrategyEvaluation {
  ticketId: string;
  ticketNumber: string;
  originalPriority: string;
  originalScore: number;
  resultPriority: string;
  resultScore: number;
  delta: number;
  matchedRules: Array<{
    id: string;
    name?: string;
    weight?: number;
    effectType?: string;
    effectPayload?: Record<string, unknown>;
  }>;
  clientInfo: Record<string, unknown> | null;
}

export interface QueueStrategyDryRunResult {
  strategy: QueueStrategy;
  queue: {
    id: string;
    name?: string;
    code?: string;
    status?: string;
  };
  sampleSize: number;
  results: QueueStrategyEvaluation[];
}

export const fetchQueueStrategies = async (): Promise<QueueStrategyListResponse> => {
  const response = await apiClient.get('/api/queue-strategies');
  return (
    response.data?.data ?? {
      strategies: [],
      capabilities: [],
    }
  );
};

export const fetchQueueStrategy = async (id: string): Promise<QueueStrategy> => {
  const response = await apiClient.get(`/api/queue-strategies/${id}`);
  return response.data?.data?.strategy as QueueStrategy;
};

export const createQueueStrategy = async (payload: QueueStrategyRequest): Promise<QueueStrategy> => {
  const response = await apiClient.post('/api/queue-strategies', payload);
  return response.data?.data?.strategy as QueueStrategy;
};

export const updateQueueStrategy = async (id: string, payload: QueueStrategyRequest): Promise<QueueStrategy> => {
  const response = await apiClient.put(`/api/queue-strategies/${id}`, payload);
  return response.data?.data?.strategy as QueueStrategy;
};

export const publishQueueStrategy = async (
  id: string,
  status: 'draft' | 'active' | 'archived',
): Promise<QueueStrategy> => {
  const response = await apiClient.post(`/api/queue-strategies/${id}/publish`, { status });
  return response.data?.data?.strategy as QueueStrategy;
};

export const deleteQueueStrategy = async (id: string): Promise<QueueStrategy> => {
  const response = await apiClient.delete(`/api/queue-strategies/${id}`);
  return response.data?.data?.strategy as QueueStrategy;
};

export const dryRunQueueStrategy = async (
  id: string,
  payload: DryRunRequest,
): Promise<QueueStrategyDryRunResult> => {
  const response = await apiClient.post(`/api/queue-strategies/${id}/dry-run`, payload);
  return response.data?.data as QueueStrategyDryRunResult;
};

export const fetchStrategyLogs = async (
  id: string,
  limit = 50,
): Promise<Array<{
  id: string;
  strategyId: string;
  queueId?: string;
  queueName?: string;
  ticketId?: string;
  ticketNumber?: string;
  userId?: string;
  username?: string;
  action: string;
  payload: Record<string, unknown>;
  createdAt: string;
}>> => {
  const response = await apiClient.get(`/api/queue-strategies/${id}/logs`, { params: { limit } });
  return response.data?.data?.logs ?? [];
};
